import { useState, useRef } from 'react';
import { search } from './api';
import type { SearchResponse } from './types';
import { SearchBar } from './components/SearchBar';
import { Synthesis } from './components/Synthesis';
import { ClaimList } from './components/ClaimList';
import { ChunkList } from './components/ChunkList';
import { AudioPlayer } from './components/AudioPlayer';

const EXAMPLE_QUERIES = [
  'What is the gap between open and closed AI models?',
  'How is the open model ecosystem evolving?',
  'What does Nathan Lambert think about open models?',
];

export default function App() {
  const [result, setResult] = useState<SearchResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inflight = useRef<AbortController | null>(null);

  async function handleSearch(query: string) {
    inflight.current?.abort();
    const ctrl = new AbortController();
    inflight.current = ctrl;
    setBusy(true);
    setError(null);
    try {
      const r = await search(query, ctrl.signal);
      setResult(r);
    } catch (e: unknown) {
      if ((e as Error).name === 'AbortError') return;
      setError((e as Error).message);
    } finally {
      if (!ctrl.signal.aborted) setBusy(false);
    }
  }

  return (
    <div className="app">
      <header className="app-header app-header-minimal">
        <nav className="header-nav">
          <a href="https://github.com/abduljaleel/resonance" target="_blank" rel="noreferrer">github</a>
          <span className="sep">·</span>
          <a href="#about">about</a>
        </nav>
      </header>

      <section className="hero">
        <div className="hero-logo" aria-label="resonance.fm">
          <span className="hero-logo-mark">R</span>
          <span className="hero-logo-word">resonance</span>
          <span className="hero-logo-tld">.fm</span>
        </div>

        <h1 className="hero-headline">
          search what was <span className="accent">claimed</span>,<br />
          by whom, <span className="accent">with what stance.</span>
        </h1>
        <p className="hero-tagline">
          <span className="marker">[*]</span>
          A natural-language search engine over AI/tech podcasts. Indexes
          assertions, hedges, denials, and retractions — not just words.
        </p>

        <SearchBar onSearch={handleSearch} busy={busy} />

        {!result && !error && (
          <>
            <div className="hero-prompts">
              <span className="hero-prompts-label">try:</span>
              {EXAMPLE_QUERIES.map(q => (
                <button
                  key={q}
                  className="hero-prompt"
                  onClick={() => handleSearch(q)}
                  disabled={busy}
                >
                  {q}
                </button>
              ))}
            </div>
            <div className="stat-strip">
              <div className="stat"><strong>1024-d</strong>bge-m3 embeddings</div>
              <div className="stat"><strong>7</strong>stance types</div>
              <div className="stat"><strong>BM25 + dense</strong>RRF hybrid retrieval</div>
              <div className="stat"><strong>Sonnet 4.6</strong>citation-grounded synthesis</div>
            </div>
          </>
        )}
      </section>

      <main className="app-main">
        {error && <div className="error">{error}</div>}

        {result && (
          <div className="results">
            <div className="intent-line">
              <span className={`intent-badge intent-${result.intent.kind}`}>
                {result.intent.kind.replace(/_/g, ' ')}
              </span>
              {result.intent.person_name && (
                <span className="intent-person">{result.intent.person_name}</span>
              )}
              {result.intent.topics.length > 0 && (
                <span className="intent-topics">
                  {result.intent.topics.map((t) => (
                    <span key={t} className="topic-tag">{t}</span>
                  ))}
                </span>
              )}
            </div>

            {result.synthesis.answer && (
              <section className="section">
                <header className="section-head">
                  <span className="title">synthesis</span>
                  <span className="meta">{result.synthesis.citations.length} citations</span>
                </header>
                <Synthesis answer={result.synthesis.answer} citations={result.synthesis.citations} />
              </section>
            )}

            {result.claims.length > 0 && (
              <section className="section">
                <header className="section-head">
                  <span className="title">cited claims</span>
                  <span className="meta">{result.claims.length} found</span>
                </header>
                <ClaimList claims={result.claims} />
              </section>
            )}

            {result.chunks.length > 0 && (
              <section className="section">
                <header className="section-head">
                  <span className="title">transcript excerpts</span>
                  <span className="meta">hybrid retrieval · BM25 + dense + RRF</span>
                </header>
                <ChunkList chunks={result.chunks} />
              </section>
            )}
          </div>
        )}
      </main>

      <AudioPlayer />
    </div>
  );
}
